import React from "react";
import { NotificationEvent } from "../../types/api";

interface NotificationEventIconProps {
    event?: NotificationEvent;
    size?: number;
}

// Per-event icons matching the design in design/user_notifications.png:
//  - canceled_by_patient -> red calendar with an X
//  - new                 -> teal calendar with a +
//  - canceled_by_staff   -> dark calendar with a pencil
//  - noshow              -> dark calendar with a pencil (same as canceled_by_staff)
const COLORS: Record<NotificationEvent, string> = {
    canceled_by_patient: "#E07A7A",
    new: "#57BFAF",
    canceled_by_staff: "#3b5d6b",
    noshow: "#3b5d6b",
};

// Shared calendar frame (top tabs + header divider).
const CalendarFrame: React.FC = () => (
    <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </>
);

const Glyph: React.FC<{ event: NotificationEvent }> = ({ event }) => {
    switch (event) {
        case "canceled_by_patient":
            // X centered in the calendar body
            return (
                <>
                    <CalendarFrame />
                    <line x1="10" y1="14" x2="14" y2="18" />
                    <line x1="14" y1="14" x2="10" y2="18" />
                </>
            );
        case "new":
            // + centered in the calendar body
            return (
                <>
                    <CalendarFrame />
                    <line x1="12" y1="14" x2="12" y2="18" />
                    <line x1="10" y1="16" x2="14" y2="16" />
                </>
            );
        case "canceled_by_staff":
        case "noshow":
        default:
            // Pencil over the calendar body
            return (
                <>
                    <CalendarFrame />
                    <path d="M16 13.5 18.5 16 13 21.5 H10.5 V19 Z" />
                    <line x1="14.5" y1="15" x2="17" y2="17.5" />
                </>
            );
    }
};

export const NotificationEventIcon: React.FC<NotificationEventIconProps> = ({
    event = "new",
    size = 22,
}) => {
    const color = COLORS[event] ?? "#3b5d6b";
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
        >
            <Glyph event={event} />
        </svg>
    );
};
